import type { Pattern, Project, Track, VoiceEvent } from '../types';
import { getSample, getSliceBuffer, SLICE_XFADE } from './registry';

/**
 * Turns project data into scheduled voices.
 *
 * Everything here is a pure function of (project, beat range) so the exact
 * same code drives live playback and offline bounce — the only difference is
 * how far ahead we ask for events.
 */

export type PlayMode = 'pattern' | 'song';

export function patternBeats(p: Pattern): number {
  return p.length / p.division;
}

export function sceneBeats(project: Project, sceneIndex: number): number {
  const s = project.scenes[sceneIndex];
  return s ? s.bars * project.beatsPerBar : 0;
}

export function songBeats(project: Project): number {
  return project.scenes.reduce((a, _, i) => a + sceneBeats(project, i), 0);
}

/** Loop length used in pattern mode: the longest active pattern. */
export function patternModeBeats(project: Project): number {
  let max = 0;
  for (const t of project.tracks) {
    const p = project.patterns.find((x) => x.id === t.patternId);
    if (p) max = Math.max(max, patternBeats(p));
  }
  return max || 4;
}

export function loopLength(project: Project, mode: PlayMode): number {
  return mode === 'song' ? songBeats(project) || patternModeBeats(project) : patternModeBeats(project);
}

/** Which scene covers a global beat, and where that scene starts. */
export function sceneAt(project: Project, beat: number): { index: number; start: number } | null {
  let acc = 0;
  for (let i = 0; i < project.scenes.length; i++) {
    const len = sceneBeats(project, i);
    if (beat < acc + len) return { index: i, start: acc };
    acc += len;
  }
  return null;
}

function activePattern(
  project: Project,
  mode: PlayMode,
  track: Track,
  beat: number,
): { pattern: Pattern; start: number } | null {
  if (mode === 'pattern') {
    const p = project.patterns.find((x) => x.id === track.patternId);
    return p ? { pattern: p, start: 0 } : null;
  }
  const scene = sceneAt(project, beat);
  if (!scene) return null;
  const clips = project.scenes[scene.index].clips;
  // No entry at all = follow the track; an entry of null = deliberately silent.
  const id = track.id in clips ? clips[track.id]?.patternId ?? null : track.patternId;
  const p = project.patterns.find((x) => x.id === id);
  return p ? { pattern: p, start: scene.start } : null;
}

/** Solo-aware audibility. */
export function trackAudible(project: Project, track: Track): boolean {
  const anySolo = project.tracks.some((t) => t.solo);
  if (anySolo) return track.solo && !track.mute;
  return !track.mute;
}

export interface RangeOptions {
  mode: PlayMode;
  /** Global beat, inclusive. */
  from: number;
  /** Global beat, exclusive. */
  to: number;
  /** Context time that corresponds to global beat 0 of this playback run. */
  timeAtZero: number;
  rng?: () => number;
}

export function eventsForRange(project: Project, opts: RangeOptions): VoiceEvent[] {
  const { mode, from, to, timeAtZero } = opts;
  const rng = opts.rng ?? Math.random;
  const spb = 60 / project.bpm;
  const events: VoiceEvent[] = [];

  for (const track of project.tracks) {
    if (!trackAudible(project, track)) continue;
    const source = project.sources.find((s) => s.id === track.sourceId);
    if (!source || !getSample(source.id)) continue;

    // Step timings can be nudged/swung backwards, so widen the scan a little
    // and filter on the final time.
    const scanFrom = from - 1;
    const scanTo = to + 1;

    // Resolve at the window start, falling back to the end so a track that
    // only comes in partway through the window still gets scanned.
    const ctxPattern =
      activePattern(project, mode, track, from) ??
      activePattern(project, mode, track, Math.max(from, to - 1e-6));
    if (!ctxPattern) continue;
    const { pattern } = ctxPattern;
    const pBeats = patternBeats(pattern);
    if (pBeats <= 0) continue;
    const stepBeats = 1 / pattern.division;

    const firstIdx = Math.floor(scanFrom / stepBeats);
    const lastIdx = Math.ceil(scanTo / stepBeats);

    for (let gi = firstIdx; gi <= lastIdx; gi++) {
      const gBeat = gi * stepBeats;
      if (gBeat < 0) continue;

      // Re-resolve per step so scene changes land on the right bar.
      const resolved = activePattern(project, mode, track, gBeat);
      if (!resolved) continue;
      const pat = resolved.pattern;
      const localBeat = ((gBeat - resolved.start) % patternBeats(pat) + patternBeats(pat)) %
        patternBeats(pat);
      const stepIdx = Math.round(localBeat * pat.division) % pat.length;
      const step = pat.steps[stepIdx];
      if (!step || step.mute || step.slice < 0) continue;
      if (step.probability < 1 && rng() > step.probability) continue;

      const swing = pat.swing || project.swing;
      const swingOffset = stepIdx % 2 === 1 ? swing * 0.5 * stepBeats : 0;
      const startBeat = gBeat + swingOffset + step.nudge * stepBeats;
      if (startBeat < from || startBeat >= to) continue;

      const slotBeats = 1 / pat.division;
      const slotSec = slotBeats * spb;
      const naturalSec = Math.max(
        0.001,
        source.sliceEdges[step.slice + 1] - source.sliceEdges[step.slice],
      );

      let baseRate = 1;
      if (track.warp === 'repitch') baseRate = project.bpm / source.bpm;
      const semis = step.pitch + track.pitch + source.rootPitch;
      const playbackRate = Math.max(0.05, baseRate * Math.pow(2, semis / 12) * step.rate);

      let stretch = 1;
      if (track.warp === 'stretch') {
        stretch = (slotSec * playbackRate) / naturalSec;
        stretch = Math.min(8, Math.max(0.125, stretch));
      }

      const audio = getSliceBuffer({
        source,
        slice: step.slice,
        reverse: step.reverse,
        pitch: 0,
        stretch,
      });
      if (!audio) continue;
      const { buffer, preroll } = audio;

      const repeats = Math.max(1, Math.round(step.repeat));
      const subSec = slotSec / repeats;
      const soundSec = Math.max(0.01, subSec * step.gate);
      // The lead-in is played before the step's own time, so it isn't part of
      // the slice body.
      const preSec = preroll / playbackRate;
      const bodySec = Math.max(0.001, buffer.duration / playbackRate - preSec);
      // Never outlast the slice itself, and never overrun the step's gate.
      const dur = Math.min(soundSec, bodySec);
      const fadeOut = Math.min(SLICE_XFADE, dur * 0.5);

      for (let r = 0; r < repeats; r++) {
        const t = timeAtZero + (startBeat * spb) + r * subSec;
        const ramp =
          repeats === 1 || step.repeatRamp === 0
            ? 1
            : step.repeatRamp > 0
              ? 1 - step.repeatRamp + step.repeatRamp * ((r + 1) / repeats)
              : 1 + step.repeatRamp * (r / Math.max(1, repeats - 1));
        events.push({
          trackId: track.id,
          // Start early by the lead-in so the slice body still lands on the beat.
          time: t - preSec,
          buffer,
          offset: 0,
          duration: preSec + dur,
          fadeIn: preSec,
          fadeOut,
          playbackRate,
          gain: step.gain * source.gain * Math.max(0, ramp),
          pan: step.pan,
          filter: step.filter,
          cutoff: step.cutoff,
          resonance: step.resonance,
          step: stepIdx,
        });
      }
    }
  }

  events.sort((a, b) => a.time - b.time);
  return events;
}
